"use client";

import { useEffect, useState } from "react";
import type { ArticleHeroShareLink } from "../../types";
import { SocialBrandIcon } from "../../components/layout/footer/SocialBrandIcon";
import { RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { isExternalHref, resolveLinkHref } from "../../utils/links";

const isEnabled = (value?: boolean | string) => {
  if (value === undefined || value === null) return true;
  return value === true || value === "true";
};

interface ArticleHeroShareProps {
  /** Title used to fill the share intent (subject / body). */
  title: string;
  /** Optional explicit URL override; defaults to the current location after mount. */
  url?: string;
  /** Per-network toggles. Defaults to all enabled. */
  email?: boolean | string;
  linkedin?: boolean | string;
  twitter?: boolean | string;
  facebook?: boolean | string;
}

/** Renders the share row for the article hero. Lives in a client component so we can read
 * window.location after hydration — buildShareLinks during SSR has no host/path/query and
 * would otherwise return an empty array, hiding the buttons entirely. */
export function ArticleHeroShare({ title, url, email, linkedin, twitter, facebook }: ArticleHeroShareProps) {
  const [links, setLinks] = useState<ArticleHeroShareLink[]>([]);
  const locale = useLocale();

  useEffect(() => {
    const resolvedUrl = resolveLinkHref(url);
    const fallbackUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const baseUrl = resolvedUrl || fallbackUrl;
    if (!baseUrl) return;
    const needsOrigin = !/^https?:\/\//i.test(baseUrl);
    const finalUrl = needsOrigin ? `${window.location.origin}${baseUrl.startsWith("/") ? "" : "/"}${baseUrl}` : baseUrl;
    const encodedUrl = encodeURIComponent(finalUrl);
    const encodedTitle = encodeURIComponent(title);

    // Generic, brand-aligned share copy, localized via the shared UI string
    // dictionary (the bank name itself stays untranslated). Kept short and
    // neutral so the same template works for any article type (communiqué,
    // note de conjoncture, rapport, etc.) without sounding generic-spammy.
    const SOURCE = "Banque Centrale du Congo";
    const emailSubject = encodeURIComponent(`${title} — ${SOURCE}`);
    const emailBody = encodeURIComponent(
      `${t(locale, "emailGreeting")}\n\n` +
      `${t(locale, "emailInvite")} ${SOURCE} :\n\n` +
      `« ${title} »\n\n` +
      `${t(locale, "readArticlePrefix")}${finalUrl}\n\n` +
      `${t(locale, "emailSignoff")}`
    );
    const twitterText = encodeURIComponent(`${title} — ${SOURCE}`);
    const linkedinSummary = encodeURIComponent(
      `${t(locale, "publishedByThe")} ${SOURCE}.`
    );
    const linkedinSource = encodeURIComponent(SOURCE);

    const next: ArticleHeroShareLink[] = [];
    if (isEnabled(email)) next.push({ type: "email", label: t(locale, "shareByEmail"), href: `mailto:?subject=${emailSubject}&body=${emailBody}` });
    if (isEnabled(linkedin)) next.push({ type: "linkedin", label: t(locale, "shareOnLinkedIn"), href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}&summary=${linkedinSummary}&source=${linkedinSource}` });
    if (isEnabled(twitter)) next.push({ type: "twitter", label: t(locale, "shareOnX"), href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${twitterText}` });
    if (isEnabled(facebook)) next.push({ type: "facebook", label: t(locale, "shareOnFacebook"), href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${twitterText}` });
    setLinks(next);
  }, [title, url, email, linkedin, twitter, facebook, locale]);

  if (links.length === 0) return null;

  return (
    <div className="pt-2">
      <p className="text-xs xl:text-sm font-semibold uppercase tracking-[0.2em] text-[#6B7280]">{t(locale, "share")}</p>
      <div className="mt-3 flex gap-2">
        {links.map((link) => {
          const isExternal = isExternalHref(link.href);
          const iconType = link.type === "email" ? "mail" : link.type;
          return (
            <RuntimeLink
              key={link.label}
              href={link.href}
              aria-label={link.label}
              className="apa-article-share grid h-9 w-9 place-items-center rounded-full bg-[#FFC105] text-white transition-opacity hover:opacity-90"
              target={isExternal ? "_blank" : undefined}
              rel={isExternal ? "noreferrer" : undefined}
            >
              <SocialBrandIcon type={iconType as any} />
            </RuntimeLink>
          );
        })}
      </div>
    </div>
  );
}
